import { Language } from '@vae/server/dist/client'
import { ScriptableContext } from 'chart.js'
import dayjs from 'dayjs'
import React, { FC, useContext } from 'react'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { AppContext } from '../../../../contexts/AppContext'
import { getMonthString } from '../../../../modules/consumptionDataParser'
import { trpc } from '../../../../modules/trpc'
import { breakpoints } from '../../../../styles/muiThemeConfig'
import getBackgroundColor from '../../utils/chart/getBackgroundColor'
import getBorderColor from '../../utils/chart/getBorderColor'
import getPointBorderColor from '../../utils/chart/getPointBorderColor'
import { getProgressionChartOptions } from '../../utils/chart/progressionChartOptions'

interface PointsProgressChartProps {
	chartData: { date: number; points: number }[]
}

const PointsProgressChart: FC<PointsProgressChartProps> = ({ chartData }) => {
	const { t, i18n } = useTranslation('pages_saving', { keyPrefix: 'pointsProgressChart' })
	const { isDarkMode } = useContext(AppContext)

	const { data: levelThresholds, isLoading } = trpc.achievements.getLevelThresholds.useQuery(
		undefined,
		{ staleTime: 60 * 60 * 1000 }
	)

	if (isLoading || !levelThresholds) return null

	const daysInObservedMonth = dayjs().daysInMonth()

	const data = chartData.map(event => ({
		x: event.date,
		y: event.points,
	}))

	const maxValue = chartData.length > 0 ? Math.max(...chartData.map(event => event.points)) : 0

	const isLargeScreen = window.innerWidth >= breakpoints.lg

	const datasets = [
		{
			data,
			fill: true,
			borderWidth: 1.6,
			borderDash: isLargeScreen ? [8, 4] : [4, 1],
			tension: 0.4,
			pointRadius: isLargeScreen ? 4 : 2,
			pointBackgroundColor: 'white',
			pointBorderWidth: 1,
			pointHoverRadius: 6,
			pointBorderColor: (ctx: ScriptableContext<'line'>) =>
				getPointBorderColor(ctx, levelThresholds),
			borderColor: (ctx: ScriptableContext<'line'>) => getBorderColor(ctx, levelThresholds),
			backgroundColor: (ctx: ScriptableContext<'line'>) => getBackgroundColor(ctx, maxValue),
		},
	]

	const options = getProgressionChartOptions(
		daysInObservedMonth,
		maxValue,
		levelThresholds,
		isDarkMode,
		t
	)

	const currentMonth = dayjs().month() + 1
	const language = i18n.language as Language
	const monthLabel = getMonthString(currentMonth, language)

	return (
		<div className='w-full rounded-t-xl border border-text-grey-100 dark:border-surface-dark bg-white dark:bg-[#0d2d30] mx-auto flex flex-col items-center px-2 pt-4 pb-12 -mb-10'>
			<span className='text-lg font-semibold text-text-dark dark:text-white'>{t('title')}</span>

			<div className='h-60 w-full'>
				<Line data={{ datasets }} options={options} />
			</div>

			<span className='text-md text-text-grey-200 dark:text-[#dcdcdc]'>{monthLabel}</span>
		</div>
	)
}

export default PointsProgressChart
